import _ from 'lodash';
import SAlert from 'react-s-alert';
import * as queryString from 'query-string';
import Bluebird from 'bluebird';
import cookies from 'browser-cookies';

export const SET_CURRENT_USER = 'SET_CURRENT_USER';
export const EXTEND_BATCH = 'EXTEND_BATCH';
export const COMPLETE_ASSIGNMENTS = 'COMPLETE_ASSIGNMENTS';
export const SHOW_CORRUPTION_FEEDBACK = 'SHOW_CORRUPTION_FEEDBACK';
export const SHOW_POST_COMPLETION_FEEDBACK = 'SHOW_POST_COMPLETION_FEEDBACK';
export const SKIP_ASSIGNMENT = 'SKIP_ASSIGNMENT';
export const REPORT_ASSIGNMENT = 'REPORT_ASSIGNMENT';
export const FLAG_ASSIGNMENT = 'FLAG_ASSIGNMENT';
export const NEXT_ASSIGNMENT = 'NEXT_ASSIGNMENT';
export const UPDATE_CLAIMS = 'UPDATE_CLAIMS';
export const UPDATE_PROJECT_BENCHMARK_METRICS = 'UPDATE_PROJECT_BENCHMARK_METRICS';
export const COMPLETE_INSTRUCTIVE = 'COMPLETE_INSTRUCTIVE';
export const SET_BONUSES = 'SET_BONUSES';
export const DISPLAY_NEW_BONUS = 'DISPLAY_NEW_BONUS';
export const CONFIRM_READY_FOR_NEXT_TASK = 'CONFIRM_READY_FOR_NEXT_TASK';
export const AWAIT_CONFIRMATION_FOR_TASKING = 'AWAIT_CONFIRMATION_FOR_TASKING';
export const AWAIT_CONFIRMATION_FOR_SKIP_TASK = 'AWAIT_CONFIRMATION_FOR_SKIP_TASK';
export const SET_PRECALCULATED_PAY = 'SET_PRECALCULATED_PAY';

import { NextPageContext } from 'next';
import apiFetch, { ApiFetchOptions } from '@scale/frontend-lib/api-fetch';
import HTTPError from '@scale/frontend-lib/httpError';
import type { NormalizedAssignment } from '@scale/shared/assignmentJson/legacy/types/NormalizedAssignment';
import { normalizeAssignments } from '@scale/shared/assignmentJson/legacy/assignment-normalizer';
import { tryParseErrorResponse } from '@scale/frontend-lib/errorMessages';
import redirect from '@scale/frontend-lib/isomorphic-redirect';
import logger from '@scale/frontend-lib/logging';
import { isLegacyReviewAssignment } from '@scale/shared/assignmentJson/legacy/isLegacyReviewAssignment';
import { getPageLoadSingleton, getBranchSingleton } from '@scale/frontend-lib/globalSingletons';
import {
  AssignmentType,
  TaskReviewOutcome,
  pipelinev3NodeGracePeriodSeconds,
} from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import { PartialRecord } from '@scale/shared/types/utils';
import { Claim } from '@scale/shared/types/queue';
import { ICompleteAttemptBody, AttemptBehaviourLog } from '@scale/shared/types/api/complete';
import { RatePrediction } from '@scale/shared/types/endpoints/scalers/rateConsent';
import { ErrorMessageAccountsNeedsVerification } from '@scale/shared/queue/constants';

import { ITaskFeedbackEntryResponse } from '@scale/shared/taskFeedback';
import { isMobileUserAgent } from '@scale/frontend-lib/util';
import { ToolSource } from '@scale/shared/types/ToolSource';
import {
  SendTypes,
  sendTypeToReviewOutcome,
} from 'react-scale/components/scalers/tasks/common/SendTask';
import { LintMetric } from '@scale/shared/task_lint/task_linter';
import { CourseSectionInput } from 'react-scale/components/scalers/training/types';
import { InlineCommentJson } from '@scale/shared/types/comments/InlineCommentJson';
import { unleashFeatureFlagVariant } from 'react-scale/lib/featureFlag';

const PENDING_TASKS_COMBINED_PATH = '/v2/tasks/new_queue';

const BASE_URL = 'internal';
const CORP_URL = 'corp-api';
const QA_AUDIT_URL = '/qa/audit';

export async function handleErrorsAndJsonify(response: Response) {
  if (!response.ok) {
    throw new HTTPError(response.statusText, await tryParseErrorResponse(response));
  }
  return response.json();
}

function fetch<R = any, B = any>(
  pathname: string,
  prefix: string,
  options: ApiFetchOptions<B> = {},
) {
  let fetchPath = pathname.slice();
  options.returnRawResponse = !options.parseAndHandle;
  delete options.parseAndHandle;

  if (options.body) {
    options.body = JSON.stringify(options.body);

    if (!options.headers) {
      options.headers = {
        'Content-Type': 'application/json',
      };
    }
  }

  if (options.query) {
    fetchPath = `${fetchPath}?${queryString.stringify(options.query)}`;
  }
  delete options.query;

  return apiFetch<R, B>(null, `${prefix}${fetchPath}`, options);
}

export function fetchCorp<R = any, B = any>(pathname: string, options: ApiFetchOptions<B> = {}) {
  return fetch<R, B>(pathname, CORP_URL, options);
}
export function fetchApi<R = any, B = any>(pathname: string, options: ApiFetchOptions<B> = {}) {
  return fetch<R, B>(pathname, BASE_URL, options);
}

// this param 'user' is optional - if not passed, the request is made
export function fetchCurrentUser(user: $FIXME) {
  return async (dispatch: $FIXME) => {
    user = user || (await fetchApi('/logged_in_user').then(handleErrorsAndJsonify));

    dispatch({
      type: SET_CURRENT_USER,
      user,
    });
  };
}

function makeKindParam(reviewModeQueryParam: string) {
  if (reviewModeQueryParam === '0') {
    // Explicitly request no reviews
    return 'task';
  } else if (reviewModeQueryParam === '1') {
    // Explicitly request only reviews
    return 'review';
  }
  return undefined;
}

function makeSimpleParams(params: $FIXME) {
  return _.map(params, (value, key) => (value ? `&${key}=${value}` : '')).join('');
}

export function updateBenchmarkStats() {
  return async (dispatch: $FIXME, getState: () => $FIXME) => {
    const assignments = _.get(getState(), 'batch.assignments', []);

    const projectIds = _.uniq(
      assignments
        .map((assignment: $FIXME) => _.get(assignment.project, '_id', assignment.project))
        .filter((projectId: string) => !!projectId),
    );

    const projectQuery = '?projects=' + projectIds.join('&projects=');
    const perProjectBenchmarkMetrics =
      projectIds.length > 0
        ? await fetchApi(`/scaler/work/project-benchmark-stats${projectQuery}`).then(
            handleErrorsAndJsonify,
          )
        : {};

    dispatch({
      type: UPDATE_PROJECT_BENCHMARK_METRICS,
      perProjectBenchmarkMetrics,
    });
  };
}
const QUEUE_CACHE_IN_SECONDS = 10;
// Function to set the timestamp when the queue starts fetching
function setIsFetchingQueue(isFetching: boolean): void {
  const alreadyFetchingQueue = isFetchingQueue();
  if (isFetching && !alreadyFetchingQueue) {
    const timestamp = Date.now();
    localStorage.setItem('isFetchingQueueTimestamp', timestamp.toString());
  } else {
    localStorage.removeItem('isFetchingQueueTimestamp');
  }
}

// Function to get the isFetchingQueue flag from localStorage
// Returns false if it was fetched over 10 seconds ago. We use
// local storage to prevent the claims issue if a user has multiple
// tabs.
function isFetchingQueue(): boolean {
  const timestampString = localStorage.getItem('isFetchingQueueTimestamp');
  if (!timestampString) {
    return false; // No timestamp stored
  }

  const timestamp = parseInt(timestampString, 10);
  const currentTime = Date.now();

  // Check if more than 10 seconds have passed
  return currentTime - timestamp <= QUEUE_CACHE_IN_SECONDS * 1000;
}

/**
 * Ideally we fix this on the backend to support atomic claims.
 * However, this will require a major refactor that will be
 * pretty risky and time consuming.
 *
 * A few quick notes from a brainstorming session
 * https://docs.google.com/document/d/1_fSmKkxYWoJ_N7J9e-IZ0Fguh1Et_rVDt5VU74GXbNs/edit
 */
async function debounceFetchQueue(path: string) {
  try {
    while (isFetchingQueue()) {
      // If we're already fetching the queue, wait for the fetch to complete to prevent
      // conflicting claims.
      await Bluebird.delay(3000);
    }
    setIsFetchingQueue(true);
    return await fetchApi(path).then(handleErrorsAndJsonify);
  } finally {
    setIsFetchingQueue(false);
  }
}

async function fetchQueue(path: string) {
  const shouldDebounceQueue = await unleashFeatureFlagVariant({
    feature: 'fetch-queue-atomically',
  });
  if (!shouldDebounceQueue) {
    // Legacy behavior
    return await fetchApi(path).then(handleErrorsAndJsonify);
  } else {
    // Debounce behavior.
    return await debounceFetchQueue(path);
  }
}

export function fetchNextAssignments(query: $FIXME, enableException = false, queueLimit?: number) {
  if (isMobileUserAgent(window.navigator.userAgent)) {
    query.mobileAgent = 1;
  }
  const backendQuery = _.omit(query, 'review_mode', 'limit');
  if (query && query.review_mode) {
    backendQuery.kind = makeKindParam(query.review_mode);
  }
  const otherParams = makeSimpleParams(backendQuery);

  return async (dispatch: $FIXME, getState: () => $FIXME) => {
    const state = getState();
    if (_.get(state, 'batch.assignments.length', 0) > 2) {
      // even if you still have stuff in progress, should still update this stuff
      updateBenchmarkStats()(dispatch, getState);

      return; // enough things in the assignment queue, don't fetch
    }

    const url = PENDING_TASKS_COMBINED_PATH;

    const limit = queueLimit ?? 1;

    try {
      const path = `${url}?limit=${limit}${otherParams}`;
      const queue = await fetchQueue(path);
      // Frontend should never have duplicates in queue
      const assignments = _.uniqBy(queue, (a: any) => a?._id);

      // Redirect to the customer queue if the project has a redirect link
      const firstAssignment = assignments[0];
      if (
        firstAssignment?.redirectLink &&
        firstAssignment?.assignmentType === AssignmentType.Redirect
      ) {
        const redirectLink = firstAssignment.redirectLink;
        if (redirectLink) {
          window.location.href = redirectLink;
        }
        return;
      }
      try {
        if (process.browser) {
          const loadedBranch = getBranchSingleton();
          // frontend relies on backend to correctly determine the cookie, so we just check here if the cookie matches the frontend we loaded
          // if not, we refresh which will make them consistent again
          const backendExperimentalMismatch =
            cookies.get('branch') === 'scale-experimental' && loadedBranch !== 'scale-experimental';
          const frontendExperimentalMismatch =
            !cookies.get('branch') && loadedBranch === 'scale-experimental';
          if (backendExperimentalMismatch || frontendExperimentalMismatch) {
            logger.warn(
              `Refreshing user because their deploy changed old=${loadedBranch} new=${cookies.get(
                'branch',
              )}`,
            );
            window.location.reload();
          }
        }
      } catch (e) {
        logger.error(`Got error trying to determine user's correct branch in queue logic`);
      }

      const course = assignments.filter(
        (a: $FIXME) => a.assignmentType === AssignmentType.Course,
      )[0];
      if (course) {
        dispatch({
          type: TRAINING_ACTIONS.SET_COURSE,
          course,
        });
      }

      // this doesnt feel right
      dispatch({
        type: TRAINING_ACTIONS.SET_LOADING,
        isLoading: false,
      });

      dispatch({
        type: EXTEND_BATCH,
        assignments: normalizeAssignments(assignments),
      });

      dispatch(updateBenchmarkStats());
    } catch (err) {
      // 403; scaler is disabled on all tasks
      if (err.response && err.response.status === 403) {
        if (err.response.error === ErrorMessageAccountsNeedsVerification) {
          // @ts-ignore If the tasker account needs verification, redirect them to the Account > Settings page
          // that shows Persona modal for verification.
          window.location = '/account';
        } else {
          // @ts-ignore Assigning directly to location instead of location.href does not require SAME_ORIGIN
          window.location = '/forbidden';
        }
      } else if (err.response && err.response.status === 429) {
        SAlert.error(
          'You have refreshed this page too quickly, or opened many windows at the same time. ' +
            "Please slow down, and don't refresh across multiple windows. You can retry after one minute.",
          { timeout: 'none' },
        );
      } else {
        SAlert.error(err.message, { timeout: 3000 });
        if (enableException) {
          throw err;
        }
      }
    }
  };
}

export function skipBatch(
  query: $FIXME,
  batchId: $FIXME,
  skipReason: $FIXME,
  skipExplanation: $FIXME,
) {
  return async (dispatch: $FIXME, getState: () => $FIXME) => {
    const response = await fetchApi('/batch/skip', {
      method: 'POST',
      body: {
        batchId,
        info: { skipReason, skipExplanation },
      },
    }).then(handleErrorsAndJsonify);
    if (response.tooManySkips) {
      alert('You have already reached the limit on skips for the day.');
    } else {
      fetchNextAssignments(query)(dispatch, getState);
      dispatch({
        type: SKIP_ASSIGNMENT,
        response,
      });
    }
  };
}

export function skipTask(
  query: NextPageContext['query'],
  assignment: $FIXME,
  skipReason: string,
  skipExplanation: string,
) {
  const pageLoadId = getPageLoadSingleton();
  logger.warn('Skipped Task', {
    assignmentId: assignment._id,
    attemptId: assignment.attemptId,
    skipReason,
    skipExplanation,
    pageLoadId,
  });
  const info = { skipReason, skipExplanation };

  let taskIds: string[];
  let attemptIds: string[];
  let path: string;
  if (isLegacyReviewAssignment(assignment)) {
    attemptIds = [assignment.attemptId];
    if (assignment.childIds) {
      attemptIds = attemptIds.concat(_.compact(assignment.childIds));
    }
    path = '/attempts/unassign';
  } else {
    taskIds = [assignment._id];
    if (assignment.childIds) {
      taskIds = taskIds.concat(_.compact(assignment.childIds));
    }
    path = '/tasks/unassign';
  }
  return async (dispatch: $FIXME) => {
    const response = await fetchApi(path, {
      method: 'POST',
      body: {
        taskIds,
        attemptIds,
        isSkippedTask: true,
        isSkipUserInitiated: true,
        info,
      },
    }).then(handleErrorsAndJsonify);
    if (response.tooManySkips) {
      alert('You have already reached the limit on skips for the day.');
    } else {
      dispatch(fetchNextAssignments(query));
      dispatch({
        type: SKIP_ASSIGNMENT,
        response,
      });
    }
    return response;
  };
}

export function reportTask(
  query: NextPageContext['query'],
  assignment: NormalizedAssignment,
  reportReason?: string,
  reportExplanation?: string,
) {
  const pageLoadId = getPageLoadSingleton();
  logger.warn('Reported Task', {
    assignmentId: assignment._id,
    attemptId: assignment.attemptId,
    reportReason,
    reportExplanation,
    pageLoadId,
  });

  return async (dispatch: $FIXME, getState: () => $FIXME) => {
    const response = await fetchApi('/tasks/report', {
      method: 'POST',
      body: {
        taskId: assignment._id,
        reportReason,
        reportExplanation,
      },
    }).then(handleErrorsAndJsonify);
    if (response.tooManySkips) {
      alert('You have already reached the limit on reports for the day.');
      logger.warn('Task Report Limit Reached', {
        assignmentId: assignment._id,
        attemptId: assignment.attemptId,
        reportReason,
        reportExplanation,
        pageLoadId,
      });
    } else {
      fetchNextAssignments(query)(dispatch, getState);
      dispatch({
        type: REPORT_ASSIGNMENT,
        response,
      });
    }
  };
}

export function flagTask(
  query: $FIXME,
  assignment: $FIXME,
  flagCategory?: string,
  reason?: string,
) {
  const pageLoadId = getPageLoadSingleton();
  logger.warn('Flagged Task', {
    assignmentId: assignment._id,
    attemptId: assignment.attemptId,
    flagCategory,
    reason,
    pageLoadId,
  });

  return async (dispatch: $FIXME, getState: () => $FIXME) => {
    const response = await fetchApi('/tasks/flag', {
      method: 'POST',
      body: {
        taskId: assignment._id,
        flagCategory,
        reason,
      },
    }).then(handleErrorsAndJsonify);

    fetchNextAssignments(query)(dispatch, getState);
    dispatch({
      type: FLAG_ASSIGNMENT,
      response,
    });
  };
}

export function nextTask(query: $FIXME, isInQATeam: boolean = false, queueLimit?: number) {
  return async (dispatch: $FIXME, getState: () => $FIXME) => {
    try {
      // If worker belongs to a QA team, redirect to QA audit page instead of fetching next assignment
      if (isInQATeam) {
        window.location.href = QA_AUDIT_URL;
      } else {
        const state = getState();
        // will only be true if they fail an instructive benchmark,
        // in which case they should not splice their current assignment list
        if (!_.get(state, 'batch.forceFixAssignment')) {
          dispatch({ type: NEXT_ASSIGNMENT });
          await fetchNextAssignments(query, true, queueLimit)(dispatch, getState);
        }
      }
    } catch (err) {
      // HACK: `fetchNextAssignments()()` fails very often:
      // And taskers are used to reload the page when they see that the page fetching the next
      // assignments is stuck. They identify this broken state when they see the infinite spinner.
      // Submit UX PR changes this spinner with a modal and tasker hack for the infinite spinner
      // no longer work because they can't identify the broken state. So we do the reload for them.
      window.location.reload();
    }
  };
}

// TODO: Make these fields non-optional in the type, and remove the validation
const DEFAULT_FIELDS = ['task_id', 'timeSpentSecs', 'timeSpentLoadingSecs', 'oldTimeSpentSecs'];
const REQUIRED_FIELDS_BY_TYPE: PartialRecord<string, string[]> = {
  annotation: [...DEFAULT_FIELDS, 'annotations'],
  audiotranscription: DEFAULT_FIELDS,
  fixannotation: [...DEFAULT_FIELDS, 'annotation'],
  polygonannotation: [...DEFAULT_FIELDS, 'annotations'],
  lineannotation: [...DEFAULT_FIELDS, 'annotations'],
  laneannotation: [...DEFAULT_FIELDS, 'annotations'],
  pointannotation: [...DEFAULT_FIELDS, 'annotations'],
};

const validateBody = (type: string) => {
  const requiredFields = REQUIRED_FIELDS_BY_TYPE[type] || [];

  return (body: ICompleteAttemptBody) => {
    _.each(requiredFields, field => {
      if (!_.has(body, field)) {
        throw Error(`${field} is required in \`completeAttempt\``);
      }
    });
  };
};

/* ---------- Task claiming ------------------------------------------------- */

// Helper function to map API response claim data to the Claim interface
function mapClaimResponse(responseItem: $FIXME): Claim {
  const claimedUntil = new Date(responseItem.claimedUntil);
  claimedUntil.setSeconds(claimedUntil.getSeconds() - pipelinev3NodeGracePeriodSeconds);

  const maxClaim = new Date(responseItem.maxClaim || Date.now() + 1000);
  maxClaim.setSeconds(maxClaim.getSeconds() - pipelinev3NodeGracePeriodSeconds);

  return {
    claimed: responseItem.claimed,
    claimedUntil,
    maxClaim,
    idleMinutes: parseInt(responseItem.idleMinutes),
    checkedAt: new Date(responseItem.checkedAt),
    checkedAtClientTime: new Date(),
    hasWarning: responseItem.hasWarning,
    warningParams: responseItem.warningParams,
    corruptionFeedback: responseItem.corruptionFeedback,
    needsRefresh: !!responseItem.needsRefresh,
    payableTime: !_.isNil(responseItem.payableTime?.maxPayableMinutes)
      ? {
          maxPayableMinutes: parseInt(responseItem.payableTime.maxPayableMinutes),
          additionalMinutesPerTurn: !_.isNil(responseItem.payableTime.additionalMinutesPerTurn)
            ? parseInt(responseItem.payableTime.additionalMinutesPerTurn)
            : undefined,
          additionalMinutesPerRewrite: !_.isNil(
            responseItem.payableTime.additionalMinutesPerRewrite,
          )
            ? parseInt(responseItem.payableTime.additionalMinutesPerRewrite)
            : undefined,
          maxOvertimeMinutes: !_.isNil(responseItem.payableTime.maxOvertimeMinutes)
            ? parseInt(responseItem.payableTime.maxOvertimeMinutes)
            : Math.min(parseInt(responseItem.payableTime.maxPayableMinutes) * 3, 720), // fallback for backwards compatibility
        }
      : undefined,
    claimMinutes: !_.isNil(responseItem.claimMinutes)
      ? parseInt(responseItem.claimMinutes)
      : undefined,
  };
}

// Input:
// {
//   task: {id: string, subtaskVersion: number},
//   attempt: {id: string, attemptVersion: number}
// }
export function checkAssignmentClaim({
  stage = undefined,
  task = undefined,
  attempt = undefined,
  elapsedTime,
  force,
  multiReplicaIndex = undefined,
}: {
  stage?: $FIXME;
  task?: $FIXME;
  attempt?: $FIXME;
  elapsedTime: number;
  force?: boolean;
  multiReplicaIndex?: number;
}) {
  return async (dispatch: $FIXME) => {
    const { forceClaim } = queryString.parse(window.location.search);
    const body = {
      stage,
      task,
      attempt,
      elapsedTime,
      usingForceClaimLink: forceClaim === '1',
      multiReplicaIndex,
    };
    const {
      tasks,
      attempts,
      stages,
      currentPageHasEditLock,
      otherUserHasLock,
      canOverride,
      hasForcePermission,
      needsRefresh,
    } = await fetchApi('/tasks/check_claim', {
      method: 'POST',
      body,
    }).then(handleErrorsAndJsonify);

    // If the user has force permissions and force is enabled don't refresh claims.
    if (hasForcePermission && force) {
      return;
    }

    const claims: PartialRecord<string, Claim> = {};
    _.each(_.concat(tasks, attempts, stages), a => {
      claims[a.id] = mapClaimResponse(a);
    });
    dispatch({
      type: UPDATE_CLAIMS,
      claims,
      currentPageHasEditLock,
      otherUserHasLock,
      canOverride,
      needsRefresh,
    });
  };
}

// Input:
// {
//   tasks: {id: string, subtaskVersion: number}[],
//   attempt: {id: string, attemptVersion: number}[]
// }
export function refreshAssignmentClaims({
  stages,
  tasks,
  attempts,
  elapsedTime,
  force,
}: {
  stages: $FIXME;
  tasks: $FIXME;
  attempts: $FIXME;
  elapsedTime: number;
  force?: boolean;
}) {
  return async (dispatch: $FIXME) => {
    const body = { reclaim: { tasks, attempts, stages }, elapsedTime };
    const response = await fetchApi('/tasks/check_claim', {
      method: 'POST',
      body,
    }).then(handleErrorsAndJsonify);

    // If the user has force permissions and force is enabled don't refresh claims.
    if (response.hasForcePermission && force) {
      return;
    }

    const claims: PartialRecord<string, Claim> = {};
    _.each(_.concat(response.tasks, response.attempts, response.stages), a => {
      claims[a.id] = mapClaimResponse(a);
    });

    dispatch({ type: UPDATE_CLAIMS, claims });
  };
}

/* ---------- Task completion ----------------------------------------------- */

export function completeAttempt(query: $FIXME, type: string, body: ICompleteAttemptBody = {}) {
  if (!body.timeSpentLoadingSecs) {
    // TODO (steven): remove this line after updating other task types
    body.timeSpentLoadingSecs = -1; // in case i missed anything
  }
  validateBody(type)(body);
  body.toolSource = ToolSource.Classic;

  return async (dispatch: $FIXME, getState: () => $FIXME) => {
    const isAsync = type === TaskType.Categorization;

    const opts = {
      body,
      method: 'POST',
    };

    /** @type {Record<string, any>} */
    let response;

    if (isAsync) {
      const attemptSubmitAsync = async () => {
        response = await fetchApi<any, ICompleteAttemptBody>(`/complete/${type}`, opts).then(
          handleErrorsAndJsonify,
        );
        fetchNextAssignments(query)(dispatch, getState);
      };

      await attemptSubmitAsync();
    } else {
      response = await fetchApi(`/complete/${type}`, opts).then(handleErrorsAndJsonify);

      if (!response) {
        return;
      }
    }

    if (response.trainingAttempt) {
      dispatch({
        type: COMPLETE_INSTRUCTIVE,
        instructiveResponse: response.trainingAttempt,
      });
    }

    if (response.precalculatedPay) {
      dispatch({
        type: SET_PRECALCULATED_PAY,
        precalculatedPay: response.precalculatedPay,
      });
    }

    return response;
  };
}

export function completeSupplementalCourse(query: $FIXME) {
  return async (dispatch: $FIXME, getState: () => $FIXME) => {
    dispatch({
      type: NEXT_ASSIGNMENT,
    });

    dispatch({
      type: TRAINING_ACTIONS.SET_COURSE_PROGRESS,
      courseProgress: { currentSection: -1 },
    });

    const state = getState();
    const nextCourse = state.batch.assignments.filter(
      (a: $FIXME) => a.assignmentType === AssignmentType.Course,
    )[0];
    dispatch({
      type: TRAINING_ACTIONS.SET_COURSE,
      course: nextCourse,
    });
    if (!nextCourse) {
      fetchNextAssignments(query)(dispatch, getState);
    }
  };
}

export interface ICompleteReviewAssignmentArgs {
  /**
   * True if this is an accept or fix review. False if this is a reject review.
   * Meaningless if trueReviewOutcome is provided
   *
   * @deprecated Prefer trueReviewOutcome
   */
  isCorrect: boolean;
  timeSpentSecs: number;
  populateHypothesis: $FIXME;
  oldTimeSpentSecs: number;
  timeSpentLoadingSecs: number;
  /**
   * If provided, overrides the inferred review outcome
   */
  trueReviewOutcome?: TaskReviewOutcome;
  reviewChanges: $FIXME;
  benchmark?: $FIXME;
  comments: $FIXME;
  fullstorySession?: $FIXME;
  extra: $FIXME;
  auditResult: string;
  categorizationResult?: $FIXME;
  taskFeedback?: ITaskFeedbackEntryResponse[];
  sendType?: SendTypes;
  linterMetrics?: LintMetric[];
  documentRotation?: number;
  documentRotationPerPage?: Record<number, number>;
  attemptBehaviourLog?: AttemptBehaviourLog;
  enabledFeatures?: string[];
  nodeId?: string;
  attemptVersion?: number;
  taskId?: string;
  multiViewNodes?: string[];
}

// NOTES: this function mutates the provided query object
export function completeReviewAssignment(
  query: $FIXME,
  assignment: $FIXME,
  {
    isCorrect,
    timeSpentSecs,
    populateHypothesis,
    oldTimeSpentSecs,
    timeSpentLoadingSecs,
    trueReviewOutcome,
    reviewChanges,
    benchmark,
    comments,
    fullstorySession,
    extra,
    auditResult,
    /** HACK Only used for Zoox steam categorization */
    categorizationResult,
    taskFeedback,
    sendType,
    linterMetrics,
    documentRotation,
    documentRotationPerPage,
    attemptBehaviourLog,
    enabledFeatures,
    multiViewNodes,
  }: ICompleteReviewAssignmentArgs,
  onSubmitSuccess = () => {},
) {
  return async (dispatch: $FIXME) => {
    const body = {
      attemptId: assignment.attemptId,
      reviewTimeSecs: timeSpentSecs,
      reviewOldTimeSecs: oldTimeSpentSecs,
      reviewTimeLoadingSecs: timeSpentLoadingSecs,
      attemptVersion: assignment.attemptVersion,
      trueReviewOutcome:
        trueReviewOutcome ??
        (sendType !== undefined ? sendTypeToReviewOutcome(sendType) : undefined),
      reviewResult: isCorrect ? 'correct' : 'incorrect',
      populateHypothesis,
      reviewChanges,
      benchmark,
      comments,
      fullstorySession,
      extra,
      auditResult,
      categorizationResult,
      taskFeedback,
      toolSource: ToolSource.Classic,
      sendType: sendType !== undefined ? SendTypes[sendType] : undefined,
      linterMetrics,
      documentRotation,
      documentRotationPerPage,
      attemptBehaviourLog,
      enabledFeatures,
      ontology: reviewChanges && assignment.params?.ontology,
      multiViewNodes,
    };

    // TODO: Type this API and use that to inform the type of body
    const response = await fetchApi(`/complete/review/${assignment.type}`, {
      body,
      method: 'POST',
    }).then(handleErrorsAndJsonify);

    if (!response) {
      return;
    }

    // This hook lets you execute code right after the HTTP call to /complete/review/:task_type succeeds,
    // but before the next batch of assignments is fetched
    await onSubmitSuccess();
    logger.log(
      `Succesfully completed review: ${assignment.attemptId} with taskFeedback ${taskFeedback}`,
    );

    const corruptionAccuracy = _.get(response, 'corruptionFeedback.corruptionAccuracy');

    // If we want to show tasker a dynamic modal before they continue
    const postCompletionFeedback = response?.postCompletionFeedback;
    if (corruptionAccuracy === 0 || corruptionAccuracy) {
      dispatch({
        type: SHOW_CORRUPTION_FEEDBACK,
        corruptionFeedback: response.corruptionFeedback,
        corruptionFeedbackAttempt: response.attempt,
        confidenceAction: response.confidenceAction,
        reviewTimeSecs: body.reviewTimeSecs,
      });
    } else {
      if (response.trainingAttempt) {
        dispatch({
          type: COMPLETE_INSTRUCTIVE,
          instructiveResponse: response.trainingAttempt,
        });
      }

      if (response.precalculatedPay) {
        dispatch({
          type: SET_PRECALCULATED_PAY,
          precalculatedPay: response.precalculatedPay,
        });
      }

      if (postCompletionFeedback) {
        dispatch({
          type: SHOW_POST_COMPLETION_FEEDBACK,
          postCompletionFeedback,
        });
      }

      // don't go and fetch new stuff if we have to correct our thing
      if (_.get(response.trainingAttempt, 'submitted') === false) {
        return response;
      }

      query = _.omit(query, 'lidarId'); // If we just finished a review, there is no reason to reload the same task
      if (response.confidenceAction) {
        redirect(null, `/corrected_reviews?confidenceAction=${response.confidenceAction}`);
      }
    }
    return response;
  };
}

export function onExit(query: $FIXME, assignments: NormalizedAssignment[]) {
  const exitTask = async (attemptId: string, type: TaskType | undefined, numChildNodes: number) => {
    const result = await fetchApi(`/complete/exit/${type}`, {
      body: {
        attemptId,
        numChildNodes,
      },
      method: 'POST',
    }).then(handleErrorsAndJsonify);

    return result;
  };

  return async (dispatch: $FIXME) => {
    await Bluebird.map(
      assignments,
      async assignment => {
        const parentId = assignment.id || assignment.attemptId || assignment.task;
        if (!parentId) {
          return;
        }

        const childIds = _.compact(assignment.childIds || []);
        const result = await exitTask(parentId, assignment.type, childIds.length);
        if (!result) {
          return;
        }

        if (result.precalculatedPay) {
          dispatch({
            type: SET_PRECALCULATED_PAY,
            precalculatedPay: result.precalculatedPay,
          });
        }

        const promises = [];
        for (const id of childIds) {
          promises.push(exitTask(id, assignment.type, childIds.length));
        }
        const responses = await Promise.all(promises);
        for (const response of responses) {
          if (!response) {
            return;
          }
        }
      },
      { concurrency: 3 },
    );
  };
}

export interface ISubmitAssignmentResult {
  isCorrect: boolean;
  timeSpentSecs: number;
  populateHypothesis: boolean;
  oldTimeSpentSecs: number;
  timeSpentLoadingSecs?: number;
  reviewChanges: $FIXME;
  reviewInlineComments?: InlineCommentJson[];
  benchmark?: $FIXME;
  comments?: string;
  fullstorySession?: string;
  extra?: $FIXME;
  auditResult?: $FIXME;
  trueReviewOutcome?: TaskReviewOutcome;
  taskFeedback?: ITaskFeedbackEntryResponse[];
}

const submitAssignment = async (
  assignment: { attemptId?: string; attemptVersion?: number; type?: TaskType },
  {
    isCorrect,
    timeSpentSecs,
    populateHypothesis,
    oldTimeSpentSecs,
    timeSpentLoadingSecs,
    reviewChanges,
    benchmark,
    comments,
    fullstorySession,
    extra,
    auditResult,
    taskFeedback,
  }: ISubmitAssignmentResult,
) => {
  const body = {
    attemptId: assignment.attemptId,
    reviewTimeSecs: timeSpentSecs,
    reviewOldTimeSecs: oldTimeSpentSecs,
    reviewTimeLoadingSecs: timeSpentLoadingSecs,
    attemptVersion: assignment.attemptVersion,
    reviewResult: isCorrect ? 'correct' : 'incorrect',
    populateHypothesis,
    reviewChanges,
    benchmark,
    comments,
    fullstorySession,
    extra,
    auditResult,
    taskFeedback,
  };

  await fetchApi(`/complete/review/${assignment.type}`, {
    body,
    method: 'POST',
  }).then(handleErrorsAndJsonify);
};

export function completeReviewAssignments(
  query: $FIXME,
  assignments: { attemptId?: string; attemptVersion?: number; type?: TaskType }[],
  results: ISubmitAssignmentResult[],
) {
  return async (dispatch: $FIXME) => {
    await Bluebird.map(
      assignments,
      async (assignment, i) => {
        try {
          await submitAssignment(assignment, results[i]);
        } catch (error) {
          // noop
        }
      },
      { concurrency: 3 },
    );

    dispatch({ type: COMPLETE_ASSIGNMENTS, assignments });

    query = _.omit(query, 'lidarId'); // If we just finished a review, there is no reason to reload the same task

    dispatch(fetchNextAssignments(query));
  };
}

/* ---------- Training courses ---------------------------------------------- */

export const TRAINING_ACTIONS = {
  SET_LOADING: 'TRAINING_ACTIONS/SET_LOADING',
  SET_COURSE: 'TRAINING_ACTIONS/SET_COURSE',
  SET_COURSE_PROGRESS: 'TRAINING_ACTIONS/SET_COURSE_PROGRESS',
  SET_COURSE_ERROR: 'TRAINING_ACTIONS/SET_COURSE_ERROR',
};

function randomizedCertificationCourseSectionsIfNeeded(course: any, courseProgress: any) {
  // Certification course section randomization hack
  const certificationRandomizationSections = courseProgress?.certificationInfo?.randomizedSections;
  if (certificationRandomizationSections && certificationRandomizationSections.length > 0) {
    course.sections = certificationRandomizationSections;
  }
}

// handle potential overrides in course progress
function overrideCourseSectionsIfNeeded(course: any, courseProgress?: any) {
  // Overriding course section hack
  const overridingSections = courseProgress?.overridingSections;
  if (overridingSections && overridingSections.length > 0) {
    course.sections = overridingSections;
  }
}

export function fetchCourse(courseName: string, tagUser?: string) {
  return async (dispatch: $FIXME) => {
    dispatch({
      type: TRAINING_ACTIONS.SET_LOADING,
      isLoading: true,
    });

    let failed = false;
    const opts = {};
    let url = `/courses/${encodeURIComponent(courseName)}`;
    if (tagUser) {
      url += `?tag=${tagUser}`;
    }
    const resp = await fetchApi(url, opts)
      .then(handleErrorsAndJsonify)
      .catch(r => {
        failed = true;
        return r.response;
      });

    if (failed) {
      dispatch({
        type: TRAINING_ACTIONS.SET_LOADING,
        isLoading: false,
      });

      dispatch({
        type: TRAINING_ACTIONS.SET_COURSE_ERROR,
        courseError: resp.error || 'There was an error loading the course',
      });

      return;
    }

    const { course, courseProgress } = resp;
    randomizedCertificationCourseSectionsIfNeeded(course, courseProgress);
    overrideCourseSectionsIfNeeded(course, courseProgress);

    dispatch({
      type: TRAINING_ACTIONS.SET_COURSE,
      course,
    });

    dispatch({
      type: TRAINING_ACTIONS.SET_COURSE_PROGRESS,
      courseProgress,
    });

    dispatch({
      type: TRAINING_ACTIONS.SET_LOADING,
      isLoading: false,
    });
  };
}

export function beginCourse(courseId: string, permissionId: string) {
  return async (dispatch: $FIXME, getState: () => $FIXME) => {
    dispatch({
      type: TRAINING_ACTIONS.SET_LOADING,
      isLoading: true,
    });

    const opts = { method: 'POST', body: { permissionId } };
    const resp = await fetchApi(`/courses/${courseId}/begin`, opts).then(handleErrorsAndJsonify);

    const state = getState();
    const course = state.training.course;
    const { courseProgress } = resp;
    randomizedCertificationCourseSectionsIfNeeded(course, courseProgress);
    overrideCourseSectionsIfNeeded(course, courseProgress);

    dispatch({
      type: TRAINING_ACTIONS.SET_COURSE,
      course,
    });

    dispatch({
      type: TRAINING_ACTIONS.SET_COURSE_PROGRESS,
      courseProgress,
    });

    dispatch({
      type: TRAINING_ACTIONS.SET_LOADING,
      isLoading: false,
    });
  };
}

// Admin only
export function skipCourse(query: $FIXME, courseId: string) {
  return async (dispatch: $FIXME, getState: () => $FIXME) => {
    dispatch({
      type: TRAINING_ACTIONS.SET_LOADING,
      isLoading: true,
    });

    const opts = { method: 'POST' };
    await fetchApi(`/courses/${courseId}/skip`, opts).then(handleErrorsAndJsonify);

    dispatch({
      type: TRAINING_ACTIONS.SET_LOADING,
      isLoading: false,
    });

    dispatch({
      type: NEXT_ASSIGNMENT,
    });

    const state = getState();
    const nextCourse = state.batch.assignments.filter(
      (a: $FIXME) => a.assignmentType === AssignmentType.Course,
    )[0];
    dispatch({
      type: TRAINING_ACTIONS.SET_COURSE,
      course: nextCourse,
    });
    if (!nextCourse) {
      fetchNextAssignments(query)(dispatch, getState);
    }
  };
}

export function continueCourse(courseProgressId: string, sectionInput?: CourseSectionInput) {
  return async (dispatch: $FIXME) => {
    dispatch({
      type: TRAINING_ACTIONS.SET_LOADING,
      isLoading: true,
    });

    const opts = {
      method: 'POST',
      body: sectionInput,
    };
    const resp = await fetchApi(`/courses/${courseProgressId}/continue`, opts).then(
      handleErrorsAndJsonify,
    );
    const { courseProgress } = resp;
    const course = courseProgress.course;
    overrideCourseSectionsIfNeeded(course, courseProgress);

    dispatch({
      type: TRAINING_ACTIONS.SET_COURSE_PROGRESS,
      courseProgress: resp.courseProgress,
    });

    dispatch({
      type: TRAINING_ACTIONS.SET_LOADING,
      isLoading: false,
    });
  };
}

export function certificationTimeRemainingSecsSync(
  courseProgressId: string,
  certificationTimeRemainingSecs: number,
) {
  return async (dispatch: $FIXME) => {
    const opts = { method: 'POST', body: { certificationTimeRemainingSecs } };
    const resp = await fetchApi(
      `/courses/${courseProgressId}/certificationTimeRemainingSecsSync`,
      opts,
    ).then(handleErrorsAndJsonify);
    dispatch({
      type: TRAINING_ACTIONS.SET_COURSE_PROGRESS,
      courseProgress: resp.courseProgress,
    });
  };
}

export function fetchBonuses(first = false) {
  return async (dispatch: $FIXME) => {
    const opts = { method: 'GET' };
    const bonuses = await fetchApi('/scaler/work/bonuses/current_and_completed', opts).then(
      handleErrorsAndJsonify,
    );
    dispatch({
      type: SET_BONUSES,
      bonuses,
      first,
    });
  };
}

export function displayNewBonus(bonus: $FIXME) {
  return async (dispatch: $FIXME) => {
    dispatch({
      type: DISPLAY_NEW_BONUS,
      bonus,
    });
  };
}

export function acceptProjectPricing(projectId: string, rate: RatePrediction) {
  return async () => {
    await fetchApi(`/rateconsent/accept`, { method: 'POST', body: { projectId, rate } }).then(
      handleErrorsAndJsonify,
    );

    window.location.reload();
    await Bluebird.delay(5000);
  };
}

export function rejectProjectPricing(projectId: string, rate: RatePrediction) {
  return async () => {
    await fetchApi(`/rateconsent/reject`, { method: 'POST', body: { projectId, rate } }).then(
      handleErrorsAndJsonify,
    );

    window.location.reload();
    await Bluebird.delay(5000);
  };
}

export function awaitConfirmationForNextTask() {
  return async (dispatch: $FIXME) => {
    dispatch({
      type: AWAIT_CONFIRMATION_FOR_TASKING,
    });
  };
}

export function awaitConfirmationForSkipTask(
  query: NextPageContext['query'],
  assignment: $FIXME,
  skipReason: string,
  skipExplanation: string,
) {
  const pageLoadId = getPageLoadSingleton();
  logger.warn('Skipped Task', {
    assignmentId: assignment._id,
    attemptId: assignment.attemptId,
    skipReason,
    skipExplanation,
    pageLoadId,
  });
  const info = { skipReason, skipExplanation };

  let taskIds: string[];
  let attemptIds: string[];
  let path: string;
  if (isLegacyReviewAssignment(assignment)) {
    attemptIds = [assignment.attemptId];
    if (assignment.childIds) {
      attemptIds = attemptIds.concat(_.compact(assignment.childIds));
    }
    path = '/attempts/unassign';
  } else {
    taskIds = [assignment._id];
    if (assignment.childIds) {
      taskIds = taskIds.concat(_.compact(assignment.childIds));
    }
    path = '/tasks/unassign';
  }
  return async (dispatch: $FIXME) => {
    const response = await fetchApi(path, {
      method: 'POST',
      body: {
        taskIds,
        attemptIds,
        isSkippedTask: true,
        isSkipUserInitiated: true,
        info,
      },
    }).then(handleErrorsAndJsonify);
    if (response.tooManySkips) {
      alert('You have already reached the limit on skips for the day.');
    } else {
      dispatch({
        type: AWAIT_CONFIRMATION_FOR_SKIP_TASK,
      });
    }
    return response;
  };
}

export function fetchNextAssignmentsOnSkip(query: NextPageContext['query']) {
  return async (dispatch: $FIXME) => {
    dispatch(fetchNextAssignments(query));
    dispatch({
      type: SKIP_ASSIGNMENT,
    });
  };
}

export function confirmReadyForNextTask() {
  return async (dispatch: $FIXME) => {
    dispatch({
      type: CONFIRM_READY_FOR_NEXT_TASK,
    });
  };
}
